/* eslint-disable no-unused-vars */
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';
import Connection from "@/models/Connection";
import Node from "@/models/Node";
import { PacketType } from '@/models/Packet';
import ProjectServer from '@/models/ProjectServer';
import StringPacking from '@/models/StringPacking';
import Tag from '@/models/Tag';
import TimelineAccess from "@/models/TimelineAccess";
import { TimelinePermissions } from "@/models/TimelineAccess";
import { SendPacket } from '@/networking/SendPacket';
import Mutations from '@/store/mutations';
import store from '@/store/store';
import ColorUtility from '@/util/ColorUtility';
import FileUtil from '@/util/FileUtil';
import StringUtil from '@/util/StringUtil';
import { CalculateNodeTextSize } from '@/util/TextSize';
import { idRetriever } from '@/util/IdRetriever';
import TimelineSyncRequests from '@/networking/TimelineSync';


function log(s: string) 
{
    console.log(`PEO: ${s}`);
}


export default class Timeline 
{
    nodeIDs: number[] = []
    name = ""
    description = ""
    comments = ""
    id = -1
    tags: Tag[] = []
    accessList = new Map<string, TimelineAccess>() // key is userid
    eventOrderings = new Map<string, number[]>()
    eraOrderings = new Map<string, number[]>()
    folderid: number | null = null;

    /**
     * Checks whether the current user has the specified timeline permission for this timeline.
     * NOTE: this does not check anything about the current user's project permissions.
     */
    hasPermission(permission: TimelinePermissions, userid: string): Boolean 
    {
        return TimelinePermissions.check(this.getPermission(userid), permission);
    }

    getPermission(userid: string): TimelinePermissions 
    {
        if (this.accessList.has(userid.toLowerCase())) 
        {
            const access = this.accessList.get(userid.toLowerCase());
            if (access != null) 
            {
                return access.Permission;
            }
        }
        return TimelineAccess.Permissions.None;
    }

    /**
     * Returns a list of node IDs, in order.
     * If no order is found with that name, it will just return a chronological ordering of all events.
     * 
     * NOTE: an ID's absence from an ordering would mean that event should be skipped (at least that's my recommendation);
     *       alternatively the rest of the events absent from the ordering could be put at the end in chronological order,
     *       leaving it up to the presenter to stop wherever they want.
     */
    public getEventOrdering(name: string = "default"): number[] 
    {
        let ordering = this.eventOrderings.get(name);
        if (ordering === undefined)
        {
            // despite the inefficiency here... I don't think it's worth optimizing.
            // map to nodes
            let nodes = this.nodeIDs.map((n) => (store.getters.nodes.get(n)));
            // remove eras
            nodes = nodes.filter((n) => (n.isEvent()));
            // sort chrono
            nodes = nodes.sort((n1, n2) => 
            {
                const v1 = n1.dateStart.valueOf();
                const v2 = n2.dateStart.valueOf();
                return (v1 > v2) ? 1 : ((v1 < v2) ? -1 : 0);
            });
            // map back to ids
            ordering = nodes.map((n) => (n.nodeid));
        }

        // TODO: sanity check filter... I think the backend lacks a fix for this.
        ordering = ordering.filter((id) => 
        {
            if (!store.getters.nodes.has(id)) return false;

            const n = store.getters.nodes.get(id) as Node;
            if (!n.isEvent()) return false;
            // if (n.tags.length > 0)
            // {
            //     if (n.tags[0].filtered) return false;
            // }
            return true;
        });

        return ordering;
    }

    /**
     * Returns whether or not a specific event order exists with the given name.
     * If this is false for the current selected event ordering (which should always be the ordering named "default" 
     * while we aren't supporting multiple orderings in the UI) then there's no need to add new events to the ordering,
     * because the implicit ordering will get generated by the GetEventOrdering method. This reduces unnecessary
     * database storage and server traffic for this feature, reducing it to zero for anyone who doesn't actually care
     * to change the event order from chronological.
     */
    public hasEventOrdering(name: string = "default"): Boolean 
    {
        return this.eventOrderings.has(name);
    }

    /**
     * This will implicitly create if the name does not exist.
     * This will implicitly delete if the newOrder is null.
     */
    public setEventOrdering(newOrder: number[], name: string = "default", sendToServer: Boolean = true) 
    {
        if (newOrder == null && this.eventOrderings.has(name)) 
        {
            this.eventOrderings.delete(name);
        }
        else 
        {
            this.eventOrderings.set(name, newOrder); // this will add or replace
        }

        if (sendToServer) 
        {
            SendPacket(PacketType.TimelineUpdateEventOrdering,
                {
                    timelineid: this.id,
                    name: name,
                    ordering: newOrder
                });
        }
    }

    /**
     * When a new event is added, this should be called to check if it needs to be patched in to 
     * any existing custom ordering and do the necessary patch.
     */
    public patchEventOrdering(newNode: Node, name: string = "default")
    {
        if (newNode.isEvent() && this.hasEventOrdering(name)) 
        {
            const ordering = this.getEventOrdering(name);
            if (!(ordering.includes(newNode.nodeid))) 
            {
                // map to nodes
                const nodes = this.nodeIDs.map((n) => (store.getters.nodes.get(n)));
                let shortestGapBefore = Number.MAX_SAFE_INTEGER;
                let insertAfterId = -1;
                const newVal = newNode.dateStart.valueOf();
                for (const node of nodes) 
                {
                    const val = newVal - node.dateStart.valueOf();
                    if (val >= 0 && val < shortestGapBefore) 
                    {
                        shortestGapBefore = val;
                        insertAfterId = node.nodeid;
                    }
                }
                
                const i = ordering.indexOf(insertAfterId) + 1;
                if (i <= 0) 
                {
                    ordering.unshift(newNode.nodeid);
                    this.setEventOrdering(ordering, name);
                }
                else if (i >= ordering.length) 
                {
                    ordering.push(newNode.nodeid);
                    this.setEventOrdering(ordering, name);
                }
                else 
                {
                    let newOrdering = ordering.slice(0, i);
                    newOrdering.push(newNode.nodeid);
                    newOrdering = newOrdering.concat(ordering.slice(i, ordering.length));
                    this.setEventOrdering(newOrdering, name);
                }
            }
        }
    }

    /**
     * For moving one nodeid to a new index in an existing event ordering.
     */
    public changeEventOrdering(nodeid: number, newIndex: number, name: string = "default") 
    {
        const ordering = this.getEventOrdering(name);
        ordering.remove(nodeid);
        if (newIndex <= 0) 
        {
            ordering.unshift(nodeid);
            this.setEventOrdering(ordering, name);
        }
        else if (newIndex >= ordering.length) 
        {
            ordering.push(nodeid);
            this.setEventOrdering(ordering, name);
        }
        else 
        {
            let newOrdering = ordering.slice(0, newIndex);
            newOrdering.push(nodeid);
            newOrdering = newOrdering.concat(ordering.slice(newIndex, ordering.length));
            this.setEventOrdering(newOrdering, name);
        }
    }

    public getEraOrdering(name: string = "default"): number[] 
    {
        let ordering = this.eraOrderings.get(name); //probably not needed for Eras? maybe we'll need it some day? -- Matt (8/15/2020)
        if (ordering == null) 
        {
            // despite the inefficiency here... I don't think it's worth optimizing.
            // map to nodes
            let nodes = this.nodeIDs.map((n) => (store.getters.nodes.get(n)));
            // remove events
            nodes = nodes.filter((n) => (n.isEra()));
            // sort chrono
            nodes = nodes.sort((n1, n2) => 
            {
                const v1 = n1.dateStart.valueOf();
                const v2 = n2.dateStart.valueOf();
                return (v1 > v2) ? 1 : ((v1 < v2) ? -1 : 0);
            });
            // map back to ids
            ordering = nodes.map((n) => (n.nodeid));
        }

        // TODO: sanity check filter... I think the backend lacks a fix for this.
        ordering = ordering.filter((id) => (store.getters.nodes.has(id)));

        return ordering;
    }

    /**
     * Exports full timeline to a docx file, including all events and eras.
     */
    public exportToDocx(fileName: string | null) 
    {
        const doc = new Document();

        let p = new Paragraph({
            alignment: AlignmentType.CENTER,
            heading: HeadingLevel.HEADING_1,
            spacing: {
                after: 400
            }
        });

        p.addRun(new TextRun({ text: this.name, bold: true, size: 38, color: 'black' }));

        doc.addSection({
            children: [p]
        });

        p = new Paragraph({
            text: this.description,
            alignment: AlignmentType.LEFT
        });

        doc.addSection({
            children: [p]
        });

        p = new Paragraph('');

        doc.addSection({
            children: [p]
        });

        let nodes = this.nodeIDs.map((n) => (store.getters.nodes.get(n)));
        nodes = nodes.sort((n1, n2) => 
        {
            const v1 = n1.dateStart.valueOf();
            const v2 = n2.dateStart.valueOf();
            return (v1 > v2) ? 1 : ((v1 < v2) ? -1 : 0);
        });

        for (const node of nodes) 
        {
            console.log(node.nodeid);
            //let node = store.getters.nodes.get(nodeid);
            console.log(node);
            if (node != null) 
            {
                console.log(`add ${node.name}`);
                node.addToDocument(doc);
            }
        }

        Packer.toBlob(doc).then((blob) => 
        {
            FileUtil.exportToFile(fileName || this.name, '.docx', blob);
        });
    }

    private duplicateNodeChain(timelineId: number, tagIdMap: Map<number, number>, nodeIdMap: Map<number, number>, index: number) 
    {
        if (this.nodeIDs.length !== 0) // nodes need duplicating
        {
            const sourceNodeId = this.nodeIDs[index];
            let sourceNode: Node = store.getters.nodes.get(sourceNodeId);
            console.log(`duplicating node ${sourceNode.name}`);
            const cb = (created: Node) => 
            {
                if (created == null) throw { error: 'Failed to create duplicated node' };

                const nodeId = created.nodeid;
                nodeIdMap.set(sourceNodeId, nodeId);
                if (nodeIdMap.size === this.nodeIDs.length) 
                {
                    // nodes finished duplicating
                    // duplicate connections
                    for (const oldNodeId of nodeIdMap.keys()) 
                    {
                        const node: Node = store.getters.nodes.get(oldNodeId);
                        const newNodeId = nodeIdMap.get(oldNodeId);
                        node.forAllRelated((childNode) => 
                        {
                            if (childNode.nodeid > node.nodeid
                                && nodeIdMap.has(childNode.nodeid)) 
                            {
                                let oldConn = new Connection(oldNodeId, childNode.nodeid);
                                oldConn = (store.getters.connections.get(oldConn.nodeid1)).get(oldConn.nodeid2);
                                const newChildNodeId = nodeIdMap.get(childNode.nodeid);
                                // create Connection between newNodeId and newChildNodeId
                                const conn = new Connection(
                                    newNodeId,
                                    newChildNodeId,
                                    oldConn.description,
                                    oldConn.references,
                                    oldConn.direction,
                                );
                                store.commit(Mutations.setConnection, conn);
                                SendPacket(PacketType.NodeAddConnection,
                                    {
                                        nodeid: conn.nodeid1,
                                        otherid: conn.nodeid2
                                    });
                                SendPacket(PacketType.UpdateConnectionText,
                                    {
                                        nodeone: conn.nodeid1,
                                        nodetwo: conn.nodeid2,
                                        value: StringPacking.Compress(conn.description, conn.references)
                                    });
                                SendPacket(PacketType.UpdateConnectionDirection,
                                    {
                                        nodeone: conn.nodeid1,
                                        nodetwo: conn.nodeid2,
                                        value: conn.direction,
                                    });
                            }
                        });
                    }
                    store.commit(Mutations.clearCopyingTimeline, { timelineid: timelineId });
                }
                else 
                {
                    this.duplicateNodeChain(timelineId, tagIdMap, nodeIdMap, index + 1);
                }
            };

            if (sourceNode.tags.length > 0) 
            {
                sourceNode = sourceNode.clone();
                console.log(`copying node with ${sourceNode.tags.length} tags`);
                console.log(sourceNode);
                const newTags: Tag[] = sourceNode.tags.map((tag) => 
                {
                    if (tagIdMap.has(tag.tagid)) 
                    {
                        const newTagId = tagIdMap.get(tag.tagid);
                        return store.getters.globalTags.get(newTagId);
                    }
                    else 
                    {
                        return tag;
                    }
                });
                sourceNode.tags = newTags;
                console.log(sourceNode);
            }
            Node.createAndWait(sourceNode, timelineId).then(cb);
        }
        else // no nodes to duplicate
        {
            store.commit(Mutations.clearCopyingTimeline, { timelineid: timelineId });
        }
    }

    private duplicateTagChain(timelineId: number, tagIdMap: Map<number, number>, index: number) 
    {
        if (this.tags.length === 0) 
        {
            console.log('Skipping tag chain');

            // tags finished duplicating
            // duplicate nodes
            const nodeIdMap = new Map<number, number>();
            this.duplicateNodeChain(timelineId, tagIdMap, nodeIdMap, 0);
        }
        else 
        {
            const tag = this.tags[index];
            console.log(`Duplicating tag ${tag.name}...`);
            idRetriever(PacketType.TagCreate, tag.name, (tagId: number) => 
            {
                console.log(`Duplicated tag ${tag.name}: ${tag.tagid} => ${tagId}`);
                tagIdMap.set(tag.tagid, tagId);
                if (tagIdMap.size === this.tags.length) 
                {
                    // tags finished duplicating
                    // duplicate nodes
                    const nodeIdMap = new Map<number, number>();
                    this.duplicateNodeChain(timelineId, tagIdMap, nodeIdMap, 0);
                }
                else 
                {
                    this.duplicateTagChain(timelineId, tagIdMap, index + 1);
                }
            });

            SendPacket(PacketType.TagCreate,
                {
                    type: 'category',
                    name: tag.name,
                    color: tag.color,
                    timelineid: timelineId
                });
        }
    }

    public async duplicate(includeAccess: boolean = false) 
    {
        store.commit(Mutations.flagCopyingTimeline, { timelineid: this.id }); // avoid starting two copies from the same source too quickly

        await TimelineSyncRequests.SyncTimeline(this.id);

        let mapNodeId: number = -1;
        for (const kvp of store.getters.mapToTimeline.entries()) 
        {
            const timelineIds: number[] = kvp[1];
            if (timelineIds.includes(this.id)) 
            {
                mapNodeId = kvp[0];
                break;
            }
        }

        let copyName = `${this.name} [Copy]`;
        // enforce length limit
        if (copyName.length > 50)
        {
            copyName = `${copyName.substr(0, 40)}... [Copy]`;
        }
        
        idRetriever(PacketType.TimelineCreate, copyName, async (timelineId: number) => 
        {
            // Sync the new timeline
            await TimelineSyncRequests.SyncTimeline(timelineId);

            store.commit(Mutations.flagCopyingTimeline, { timelineid: timelineId }); // TODO: this means it will only lock locally
            store.commit(Mutations.clearCopyingTimeline, { timelineid: this.id }); // free to start another copy from same source

            // timeline duplicated
            // duplicate tags
            const tagIdMap = new Map<number, number>();
            this.duplicateTagChain(timelineId, tagIdMap, 0);

            // duplicate TimelineAccess if desired
            if (includeAccess) 
            {
                store.commit(Mutations.flagCopyingTimeline, { timelineid: timelineId });
                for (const access of this.accessList.values()) 
                {
                    SendPacket(PacketType.TimelineUpdateAccess,
                        {
                            timelineid: timelineId,
                            access: access
                        });
                }
                store.commit(Mutations.clearCopyingTimeline, { timelineid: timelineId });
            }
        });

        SendPacket(PacketType.TimelineCreate,
            {
                mapnodeid: mapNodeId,
                name: copyName,
                description: this.description
            });
    }
}